import { Button } from "@omni/ui/components/Button/button.component";
import classnames from "classnames";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import SearchBoxComponent from "@/components/Search/SearchBar/AutocompleteSearchBox/SearchBox/searchBox.component";
import { ResultsPanel } from "@/components/Search/SearchBar/ResultsPanel/resultsPanel.container";
import { getQuery } from "@/redux/modules/value/search/search.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { KeyNames } from "@/utils/enums";
import { useScrollLock } from "@/utils/hooks/useScrollLock";
import { createKeyHandler } from "@/utils/keyboard";
import { Layer, setLayer } from "@/utils/layer";

import "./searchBar.scss";

import { SearchBarProps } from "@omni/types/components/props/searchBar.props";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { hasWindow } from "@omni/utils/dom.utils";

import { Overlay } from "@/components/General/Overlay/overlay";
import { AutocompleteRef } from "@/components/Search/SearchBar/searchBar.types";
import { useAutocompleteApi } from "@/components/Search/SearchBar/useAutocompleteApi.hook";
import { blurActiveElement } from "@/utils/blur";
import { isPageTemplate } from "@/utils/pageTemplate.utils";
import { parseUrl } from "@/utils/search/routing/configurationFromUrl";

const AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK = "autocomplete-search-box";

export const SearchBar = ({ initialProducts }: SearchBarProps) => {
    const { t } = useGranite();

    const { lock, unlock } = useScrollLock(AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK);

    const isSearchPage = isPageTemplate(PageTemplate.Search);
    const initialQuery =
        useSelector(getQuery) || (isSearchPage && hasWindow())
            ? parseUrl().q ||
              ((
                  document.getElementById(
                      "autocomplete-input"
                  ) as HTMLInputElement | null
              )?.value ??
                  "")
            : "";

    const autocompleteRef = useRef<AutocompleteRef>({
        form: null,
        input: null,
        query: initialQuery
    });
    const panelRef = useRef<HTMLDivElement>(null);

    const {
        autocomplete,
        isOpen,
        open,
        close,
        autocompleteActions,
        autocompleteState,
        items
    } = useAutocompleteApi({
        autocompleteRef: autocompleteRef,
        initialProducts: initialProducts
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        autocompleteRef.current.query = initialQuery;
        autocomplete.setQuery(initialQuery);
    }, [initialQuery]);

    const props = {
        root: autocomplete.getRootProps({
            "aria-labelledby": "submit-search-button"
        }),
        panel: autocomplete.getPanelProps({})
    };

    const handleOnClose = () => {
        close();
        blurActiveElement();
    };

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: handleOnClose
        },
        {
            keys: [KeyNames.SPACE],
            handler: (event) => {
                if (isOpen) return;

                event.preventDefault();
                open();
            }
        },
        {
            defaultExcept: [KeyNames.TAB, KeyNames.SHIFT],
            handler: open
        }
    ]);

    useEffect(() => {
        if (
            !autocompleteRef.current.form ||
            !autocompleteRef.current.input ||
            !panelRef.current
        ) {
            return;
        }

        const { onTouchStart, onTouchMove, onMouseDown } =
            autocomplete.getEnvironmentProps({
                formElement: autocompleteRef.current.form,
                inputElement: autocompleteRef.current.input,
                panelElement: panelRef.current
            });

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("touchstart", onTouchStart);
        window.addEventListener("touchmove", onTouchMove);

        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
        };
    }, [autocomplete.getEnvironmentProps]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        (isOpen ? lock : unlock)();

        const navigationTopBarElement = document.querySelector(
            "section.navigation__top-bar"
        ) as HTMLElement | null;

        if (navigationTopBarElement) {
            setLayer(navigationTopBarElement, Layer.NAVIGATION, isOpen);
        }
    }, [isOpen]);

    return (
        <>
            <div
                {...props.root}
                className={classnames(
                    "autocompleteSearchBox",
                    isOpen && "autocompleteSearchBox--open"
                )}
                onClick={open}
                onKeyDown={handleOnKeyDown}
            >
                <SearchBoxComponent
                    ref={autocompleteRef}
                    isOpen={isOpen}
                    autocomplete={autocomplete}
                    autocompleteState={autocompleteState}
                    open={open}
                    close={handleOnClose}
                    autocompleteActions={autocompleteActions}
                />
                {isOpen && (
                    <>
                        <Button
                            type={"button"}
                            color={"light-gray"}
                            className={
                                "autocompleteSearchBox__close d-none d-lg-block"
                            }
                            onClick={handleOnClose}
                            tabIndex={isOpen ? 0 : -1}
                            aria-label={t("accessibility.searchBox.close")}
                        >
                            <i className={"las la-times"}></i>
                        </Button>

                        <ResultsPanel
                            onMouseDown={props.panel.onMouseDown}
                            items={items}
                            autocompleteState={autocompleteState}
                            autocompleteActions={autocompleteActions}
                            autocompleteRef={autocompleteRef}
                        />
                    </>
                )}
            </div>
            <Overlay
                open={isOpen}
                onClose={handleOnClose}
                overlayHeader={true}
                scrollLock={false}
            >
                <Overlay.Content />
            </Overlay>
        </>
    );
};
